import React, { createContext, useCallback, useContext, useState } from "react";
// import { useUpdateTakeback } from "../../../api/tes/useUpdateTakeback";
import { useCurrentLocation } from "../../../lib/utils";
import { useTranslation } from "react-i18next";
import { useDerivedWeights } from "./useDerivedWeights";
import { useWarehouse } from "./useWarehouse";
import { useSteps } from "./useSteps";
import { TakebackQueueContext } from "../../../takeback-queue/TakebackQueueProvider";
import { AnimatePresence } from "framer-motion";
import { PinModal } from "./components/PinModal";
import { calculateEstimate } from "lib/calculateEstimate";
import AuthContext from "../../../contexts/AuthContext";

import { isInStoreVerified, isDropoffVerified } from "lib/verification-utils";
// Local weight converter that fixes the Math.ceil rounding issue
const convertWeight = (uom) => (weight) => {
  if (uom !== "lbs") return weight;
  const weightInKg = (weight * 2.20462).toFixed(2);
  return Math.round(weightInKg * 100) / 100; // Round to 2 decimal places instead of 1
};

const noop = () => null;

export const WarehouseContext = createContext({});

export const SORTED_PICTURES = {
  all: "all",
  bucket2: "bucket2_no_conforming",
  bucket3a: "bucket3a_good_quality",
  bucket3b: "bucket3b_poor_quality",
  bucket3c: "bucket3c_electronic",
  bucket3d: "bucket3d_non_lego",
};

export const WarehouseProvider = ({ children }) => {
  const { t } = useTranslation();
  const { location: currentLocation } = useCurrentLocation();

  const { add } = useContext(TakebackQueueContext);
  const { user } = useContext(AuthContext);

  const [showPinModal, setShowPinModal] = useState(false);
  const [onPinSuccessFn, setOnPinSuccessFn] = useState(noop);

  const {
    takeback,
    packagePictures,
    contentPictures,
    nonConformPictures,
    controlWeight,
    packagingWeight,
    contentWeight,
    assembledWeight,
    conformingWeight,
    sortedPictures,
    rejectedContentsPictures,
    scaleReadingPictures,
    rejectCategory,
    setTakeback,
    setPackagePictures,
    setContentPictures,
    setNonConformPictures,
    setControlWeight,
    setPackagingWeight,
    setContentWeight,
    setAssembledWeight,
    setConformingWeight,
    setSortedPictures,
    setRejectedContentsPictures,
    setScaleReadingPictures,
    setRejectCategory,
    selectedBucket,
    setSelectedBucket,
  } = useWarehouse();

  const { dropStoreMode } = useSteps();

  const createStatus = useCallback(
    (
      { status, parent_status, updated_at } = {
        updated_at: Math.floor(Date.now() / 1000),
      }
    ) => ({ status, parent_status, updated_at }),
    []
  );

  const data =
    typeof takeback?.data === "object"
      ? takeback?.data
      : JSON.parse(takeback?.data || "{}");

  const weightConverter = convertWeight(data.uom);

  const donationWeight = data.originalReplayWeight || data.replayWeight || 0;
  const estimatedWeight = data.weight;
  const donationPercentage =
    estimatedWeight > 0 ? donationWeight / estimatedWeight : 0;

  const {
    actualContentWeight,
    actualConformingWeight,
    paymentWeight,
    nonConformingWeight,
  } = useDerivedWeights({
    controlWeight,
    contentWeight,
    donationPercentage,
    conformingWeight,
    assembledWeight,
    weightConverter,
  });

  const conformWeight = actualContentWeight - nonConformingWeight;
  const verifiedReplayWeight =
    weightConverter(conformWeight) * donationPercentage;

  const setCurrentTakeback = (takeback) => {
    setTakeback(takeback);
  };
  const addPackagePicture = (picture) => {
    setPackagePictures([...packagePictures, picture]);
  };
  const deletePackagePicture = (picture) => {
    setPackagePictures(packagePictures.filter((pic) => pic !== picture));
  };
  const addContentPicture = (picture) => {
    setContentPictures([...contentPictures, picture]);
  };
  const deleteContentPicture = (picture) => {
    setContentPictures(contentPictures.filter((pic) => pic !== picture));
  };
  const addNonConformPicture = (picture) => {
    setNonConformPictures([...nonConformPictures, picture]);
  };
  const deleteNonConformPicture = (picture) => {
    setNonConformPictures(nonConformPictures.filter((pic) => pic !== picture));
  };
  const setSortedPicture = (key, picture) => {
    const newSortedPictures = { ...sortedPictures, [key]: picture };
    setSortedPictures(newSortedPictures);
  };
  const addRejectContentsPicture = (picture) => {
    setRejectedContentsPictures([picture]);
  };
  const deleteRejectContentsPicture = () => {
    setRejectedContentsPictures([]);
  };
  const addScaleReadingPicture = (picture) => {
    setScaleReadingPictures([...scaleReadingPictures, picture]);
  };
  const deleteScaleReadingPicture = (picture) => {
    setScaleReadingPictures(
      scaleReadingPictures.filter((pic) => pic !== picture)
    );
  };

  const checkInPackage = useCallback(
    async (takeback) => {
      const data =
        typeof takeback?.data === "object"
          ? takeback?.data
          : JSON.parse(takeback?.data || "{}");

      const hasReceivedStatus =
        takeback?.takeback_processing_history_list?.some(
          (history) => history.status === "checked-in"
        );

      const statuses = [];

      if (!hasReceivedStatus) {
        statuses.push(
          createStatus({ status: "checked-in", parent_status: "received" })
        );
      }
      const { packagePictures } = useWarehouse.getState();

      // TODO: consolidate this logic with updateTakeback image uploads
      const imagesToUpload = [];

      if (packagePictures.length > 0) {
        imagesToUpload.push({
          filename: "v2_package_images",
          images: packagePictures,
        });
      }

      // add checked_in_by_user_id to takebackUpdates

      const takebackUpdates = [
        {
          input: {
            data: {
              ...data,
              checked_in_by_user_id: user?.id ?? null,
            },
          },
        },
      ];

      add({ caseId: takeback?.id, statuses, imagesToUpload, takebackUpdates });
    },
    [add, createStatus, user?.id]
  );

  const startProcessingPackage = useCallback(
    async (takeback) => {
      // Only add received status if it doesn't exist in the history
      const hasReceivedStatus =
        takeback?.takeback_processing_history_list?.some(
          (history) => history.status === "checked-in"
        );
      const hasWarehouseStartedStatus =
        takeback?.takeback_processing_history_list?.some(
          (history) => history.status === "warehouse-started"
        );

      const statuses = [];

      if (!hasReceivedStatus) {
        statuses.push(
          createStatus({ status: "checked-in", parent_status: "received" })
        );
      }

      if (!hasWarehouseStartedStatus) {
        statuses.push(
          createStatus({
            status: "warehouse-started",
            parent_status: "processing",
          })
        );
      }

      add({ caseId: takeback?.id, statuses });
    },
    [add, createStatus]
  );

  const handleModalClose = () => {
    setShowPinModal(false);
    setOnPinSuccessFn(noop);
  };

  const confirmWithPin = (onPinSuccess) => {
    setOnPinSuccessFn(() => onPinSuccess);

    setShowPinModal(true);
  };

  const onPinSuccess = () => {
    onPinSuccessFn();
    handleModalClose();
  };

  const { giftRatio } = calculateEstimate(1);

  const updateTakeback = async ({
    isRejected = false,
    rejectReason,
    rejectStatus,
    rejectCategory,
    resolutionCategory,
    resolutionReason,
  } = {}) => {
    const data =
      typeof takeback?.data === "object"
        ? takeback?.data
        : JSON.parse(takeback?.data || "{}");

    // Construct the data object first
    const dataToUpdate = {
      ...data,
      unit: t("scale.weight"),
      // Use different field names based on mode
      ...(dropStoreMode
        ? {
            dropoff_packaging_weight: weightConverter(
              packagingWeight - controlWeight
            ),
            dropoff_presort_warehouse_weight:
              weightConverter(actualContentWeight),
            dropoff_general_nonconform_weight:
              weightConverter(nonConformingWeight),
            dropoff_conform_weight: weightConverter(actualConformingWeight),
          }
        : {
            presort_warehouse_weight: weightConverter(actualContentWeight),
            general_nonconform_weight: weightConverter(nonConformingWeight),
            assembled_nonconform_weight: weightConverter(
              assembledWeight - controlWeight
            ),
            postsort_weight: weightConverter(actualConformingWeight),
            conform_weight: weightConverter(actualConformingWeight),
            payment_weight: paymentWeight,
            legoValue: (giftRatio * paymentWeight).toFixed(2),
            u_lego_weight: paymentWeight,
          }),
      currency: currentLocation.country === "US" ? "USD" : "EUR",
      processed_by_user_id: user?.id ?? null,
      control_weight: weightConverter(controlWeight),
      verifiedReplayWeight,
      u_lego_uom: t("scale.weight"),
      // Preserve existing in-store verification data if it exists
      ...(data.inStoreVerifiedAt && {
        inStoreVerifiedAt: data.inStoreVerifiedAt,
        verified_by_associate: data.verified_by_associate,
      }),
      // Add resolution data if provided
      ...(resolutionCategory && {
        resolutionCategory,
        resolutionReason: resolutionReason || "",
      }),
      // Preserve originalEstimatedWeight if it exists
      ...(data.originalEstimatedWeight && {
        originalEstimatedWeight: data.originalEstimatedWeight,
      }),
      // If its been escalated include the escalated reason
      ...(rejectCategory === "escalated" && {
        escalated_reason: rejectReason,
        escalated_email: "yes",
      }),
    };

    // Determine the appropriate verification status based on whether this was in-store verified
    const isAlreadyInStoreVerified = isInStoreVerified(takeback);
    const isAlreadyDropoffVerified = isDropoffVerified(takeback);
    const isDropStoreMode = dropStoreMode; // in case we want to bypass associate verification for testing
    const verificationStatus = isAlreadyInStoreVerified
      ? createStatus({
          status: "warehouse-verified-post-instore",
          parent_status: "processed",
        })
      : isAlreadyDropoffVerified || isDropStoreMode
        ? createStatus({
            status: "warehouse-verified-post-dropoff",
            parent_status: "processed",
          })
        : createStatus({
            status: "verified",
            parent_status: "processed",
          });

    const verifiedUpdate = {
      input: {
        type_name: takeback.type_name,
        data: dataToUpdate,
        created_at: takeback.created_at,
        statuses: verificationStatus,
      },
    };
    const rejectUpdate = {
      input: {
        type_name: takeback.type_name,
        data: {
          ...dataToUpdate,
          rejectReason,
          rejectCategory,
        },
        created_at: takeback.created_at,
        statuses: createStatus({
          parent_status: "issue",
          status: rejectStatus,
        }),
      },
    };
    const takebackUpdates = [
      !isRejected || rejectCategory === "escalated" // Note: escalated is a special case where we don't want to reject the takeback (PLU-85)
        ? verifiedUpdate
        : rejectUpdate,
    ];

    // Check if takeback has checked-in status
    const hasCheckedInStatus = takeback?.takeback_processing_history_list?.some(
      (history) => history.status === "checked-in"
    );

    const statuses = [];

    // Even though we don't want to reject the takeback, we want to add the escalated status to the takeback
    // (TES looks for this to send email)
    if (rejectCategory === "escalated") {
      statuses.push(
        createStatus({
          status: "escalated",
          parent_status: "issue",
        })
      );
    }

    // Add checked-in status if it doesn't exist
    if (!hasCheckedInStatus) {
      statuses.push(
        createStatus({ status: "checked-in", parent_status: "received" })
      );
    }

    // Add warehouse-ended status only for non-rejected items
    if (
      (!isRejected && rejectStatus !== "rejected") ||
      rejectCategory === "escalated" // Note: escalated is a special case where we don't want to stop processing of takeback (PLU-85)
    ) {
      statuses.push(
        createStatus({
          status: "warehouse-ended",
          parent_status: "processed",
        })
      );
    }

    const imagesToUpload = [];

    if (packagePictures.length > 0) {
      imagesToUpload.push({
        filename: "v2_package_images",
        images: packagePictures,
      });
    }

    if (contentPictures.length > 0) {
      imagesToUpload.push({
        filename: "content_images",
        images: contentPictures,
      });
    }

    if (nonConformPictures.length > 0) {
      imagesToUpload.push({
        filename: "non_conform_images",
        images: nonConformPictures,
      });
    }

    if (rejectedContentsPictures.length > 0) {
      imagesToUpload.push({
        filename: "reject_contents_images",
        images: rejectedContentsPictures,
      });
    }

    if (scaleReadingPictures.length > 0) {
      imagesToUpload.push({
        filename: "scale_reading_images",
        images: scaleReadingPictures,
      });
    }

    const sortedImages = Object.keys(sortedPictures).map((key) => {
      return {
        filename: key,
        images: [sortedPictures[key]],
      };
    });

    if (sortedImages.length > 0) {
      imagesToUpload.push(...sortedImages);
    }

    //add to queue
    add({
      imagesToUpload,
      caseId: takeback?.id,
      takebackUpdates,
      statuses,
    });
  };

  const clearTakeback = () => {
    setTakeback(null);
    setPackagePictures([]);
    setContentPictures([]);
    setNonConformPictures([]);
    setPackagingWeight("");
    setContentWeight("");
    setAssembledWeight("");
    setConformingWeight("");
    setSortedPictures({});
    setRejectedContentsPictures([]);
    setScaleReadingPictures([]);
    setRejectCategory("");
  };

  const sellerDisplayName =
    takeback && takeback.customer_id_belongsTo
      ? `${takeback.customer_id_belongsTo.name || ""} ${takeback.customer_id_belongsTo.last_name || ""}`.trim()
      : "";
  const caseId = takeback ? takeback.id : "";

  return (
    <WarehouseContext.Provider
      value={{
        checkInPackage,
        startProcessingPackage,
        currentTakeback: takeback,
        sellerDisplayName,
        caseId,
        setCurrentTakeback,
        addPackagePicture,
        deletePackagePicture,
        packagePictures,
        controlWeight,
        setControlWeight,
        packagingWeight,
        setPackagingWeight,
        contentWeight,
        setContentWeight,
        contentPictures,
        addContentPicture,
        deleteContentPicture,
        nonConformPictures,
        addNonConformPicture,
        deleteNonConformPicture,
        assembledWeight,
        setAssembledWeight,
        conformingWeight,
        setConformingWeight,
        nonConformingWeight,
        sortedPictures,
        setSortedPicture,
        updateTakeback,
        clearTakeback,
        rejectedContentsPictures,
        deleteRejectContentsPicture,
        addRejectContentsPicture,
        scaleReadingPictures,
        addScaleReadingPicture,
        deleteScaleReadingPicture,
        confirmWithPin,
        rejectCategory,
        setRejectCategory,
        selectedBucket,
        setSelectedBucket,
      }}
    >
      <>
        {children}
        {showPinModal && (
          <AnimatePresence>
            <PinModal
              isOpen={showPinModal}
              onClose={() => handleModalClose()}
              onPinSuccess={onPinSuccess}
              title={`Override Incomplete Processing`}
              body="Enter a pin to finish with incomplete steps."
              primaryText="Confirm"
              secondaryText="Cancel"
            />
          </AnimatePresence>
        )}
      </>
    </WarehouseContext.Provider>
  );
};

export const useWarehouseContext = () => {
  const warehouseContext = useContext(WarehouseContext);
  if (!warehouseContext) {
    throw new Error(
      "No warehouse context.Did you wrap your component in WarehouseProvider?"
    );
  }

  return warehouseContext;
};
